import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import * as apiCache from "../apiCacheCalls";
import { CartDrawer } from "../components/global/CartDrawer/CartDrawerComponent";
import { PHead } from "../components/global/PHead/PHead";
import SnackBar from "../components/global/SnackBar/SnackBar";
import { CategoryScreen } from "../components/screens/S-Category/CategoryScreen";
import { useModalHook } from "../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../services/hooks/useReduxHooks";
import { fetchItemStatusRedux } from "../services/redux/http/itemStatusFetch";
import {
  LoadingActionEnum,
  decreasePendingOperations,
  pushToLoadingStack,
  setLoading,
} from "../services/redux/slices/globalComponentsToggleSlice";
import { setEntityImageLookup } from "../services/redux/slices/configSlice";
import LS from "../utils/LocalStorage";
import { LocalStorageEnum } from "../utils/enums";
import env from "../utils/env.config";
import { globalTranslationFiles } from "../utils/help/localization/localization";
import { getFranchiseSlug } from "../utils/help/navigation/navigation";
import { setAllCategoriesItemsCustomPricesPerFranchise } from "../utils/help/pricing/pricing";
import { getRestaurantStaticProps } from "../utils/help/staticProps/restaurantStaticProps";
import { CategoryType } from "../utils/types";
import { findFranchiseBySlug } from "../utils/help/franchise/findFranchiseBySlug";

const TrackingLogo = dynamic(
  async () => {
    try {
      const mod = await import(
        `../components/global/TrackingLogo/${"TrackingLogoComponent"}`
      );

      return mod;
    } catch (e) {
      console.error(e);
    }
  },
  { ssr: false }
);

function Home(props: { restaurantInfo: any; allCategories: CategoryType[] }) {
  const router = useRouter();
  const { handleModal } = useModalHook();

  const [areaAboveNavBar, setAreaAboveNavBar] = useState(100);

  const { franchise, promo, order_type } = router.query;
  const cartAndLoyaltyReducer = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const dispatch = useAppDispatch();
  const restaurantInfoRedux = useAppSelector(
    (state) => state.config.restaurantInfo
  );
  const franchiseId = useAppSelector((state) => state.cartAndLoyaltyReducer)
    .cart.franchiseId;
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const prevSelectedFranchiseIdRef = useRef<number | undefined>(
    selectedFranchise?.id
  );
  const globalSnackBar = useAppSelector(
    (state) => state.globalComponentsToggleSlice.globalSnackBar
  );
  const isMobile = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isMobile
  );
  const liveMenuCategories = useAppSelector(
    (state) => state.config.liveMenuCategories
  );

  const setCategoriesWithLookup = (categories: CategoryType[]) => {
    setAllNewCategories(categories);
    dispatch(setEntityImageLookup(categories));
  };

  /**
   * Render the SSG menu with one franchise's pricing applied.
   *
   * The clone is NOT optional. setAllCategoriesItemsCustomPricesPerFranchise
   * writes `subitem.price` in place, so handing it props.allCategories directly
   * both mutates frozen Next SSG props and bakes the first franchise's prices
   * into the shared object -- after which switching franchise applies overrides
   * on top of overridden data, and a franchise with no override of its own
   * keeps showing the previous one's price. #4166
   */
  const priceMenuForFranchise = (targetFranchiseId: number) =>
    setAllCategoriesItemsCustomPricesPerFranchise(
      JSON.parse(JSON.stringify(props.allCategories)),
      targetFranchiseId
    );

  const showMenuForFranchise = (targetFranchiseId: number) => {
    if (!props.allCategories) {
      return;
    }

    setCategoriesWithLookup(priceMenuForFranchise(targetFranchiseId));
  };

  // Priced on the first render, not from an effect. The page renders nothing
  // until the menu is set, and the effect only ran after that empty render was
  // painted — so every arrival on Home (e.g. confirming a branch in the welcome
  // drawer) flashed a blank screen while the menu was cloned and priced
  // (prepits/project#4344). The franchise effect below skips its first run,
  // which would only repeat this.
  const [allCategories, setAllNewCategories] = useState<CategoryType[] | null>(
    () => (props.allCategories ? priceMenuForFranchise(franchiseId) : null)
  );
  const isFirstFranchiseRunRef = useRef(true);

  useEffect(() => {
    const handleScroll = () => {
      const headerHeight =
        document.querySelector<HTMLElement>("#_header_wrapper")?.clientHeight ||
        0;
      const tabsBarHeight =
        document.querySelector<HTMLElement>("#previous_order_bar")
          ?.clientHeight || 0;

      // const marginBottomTabsBar = 8;
      const marginBottomTabsBar = 0;
      const areaAboveNavBars =
        tabsBarHeight + headerHeight + marginBottomTabsBar;

      if (areaAboveNavBars !== areaAboveNavBar) {
        setAreaAboveNavBar(areaAboveNavBars);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [areaAboveNavBar]);

  useEffect(() => {
    if (
      promo &&
      cartAndLoyaltyReducer.promoCode.name.length > 0 &&
      cartAndLoyaltyReducer.promoCode.valid &&
      franchise &&
      order_type
    ) {
      handleModal(true, "ReferralModal/ReferralModal", {
        preventBackdropClick: true,
      });
    }
  }, [cartAndLoyaltyReducer.promoCode.name]);

  useEffect(() => {
    if (allCategories) {
      dispatch(setEntityImageLookup(allCategories));
    }
  }, []);

  // MENU_VERSION_CHANGE is handled app-wide now (BackgroundRequests), because
  // the guest who most needs it is the one stuck on the cart. It refreshes the
  // menu lookups checkout reads; rendering the new menu stays here, where the
  // franchise pricing is applied.
  useEffect(() => {
    // `undefined` is "no live menu yet"; an empty array is a live menu with
    // nothing in it (every category deleted) and has to replace the page's.
    if (liveMenuCategories === undefined || !franchiseId) {
      return;
    }

    setAllNewCategories(
      setAllCategoriesItemsCustomPricesPerFranchise(
        JSON.parse(JSON.stringify(liveMenuCategories)),
        franchiseId
      )
    );
  }, [liveMenuCategories, franchiseId]);

  useEffect(() => {
    // The page is regenerated on demand whenever the menu changes
    // (pages/api/revalidate.ts, called by core when menuVersion is bumped),
    // so props.allCategories is always current — no localStorage copy to
    // prefer, and no need to ask the backend on every page load. #4166
    if (isFirstFranchiseRunRef.current) {
      isFirstFranchiseRunRef.current = false;
    } else {
      showMenuForFranchise(franchiseId);
    }
    let franchiseData;
    const franchiseSlug = getFranchiseSlug(franchise as string);
    if (franchiseSlug) {
      // Collision-aware: partner restaurants can share a slug (Vasko "B02");
      // prefer the branch the guest committed over the first list match.
      franchiseData = findFranchiseBySlug(
        props.restaurantInfo.franchises,
        franchiseSlug,
        selectedFranchise
      );
      if (!franchiseData && restaurantInfoRedux) {
        franchiseData = findFranchiseBySlug(
          restaurantInfoRedux.franchises,
          franchiseSlug,
          selectedFranchise
        );
      }
    } else if (!franchiseSlug && props.restaurantInfo.mainFranchise) {
      franchiseData = { ...props.restaurantInfo.mainFranchise };
    }
    if (franchiseData) {
      // Any menu cached by a previous build of this app is now dead weight —
      // the served HTML is the source of truth. Clear it once so a returning
      // guest cannot be shown a stale menu from their own storage.
      LS._delete(LocalStorageEnum.MENU_DATA);
      LS._delete(LocalStorageEnum.ITEMS_DATA);
      if (franchiseSlug) {
        dispatch(
          fetchItemStatusRedux({
            restaurantId: props.restaurantInfo.id,
            slug: franchiseSlug,
          })
        );
        // The menu in props already IS this payload. /category/details
        // returns an identical body with or without franchiseSlug — verified
        // against a restaurant that has a real per-franchise override
        // (brownnose/B01): 12 items, 0 differing. The override travels inside
        // customFranchiseSubItemPrices and is applied right here, client side,
        // which is why the fetched copy needed the same call anyway. So this
        // asked the backend, on every single page load, for bytes the served
        // HTML already contained. #4166
      }
      if (props.restaurantInfo.config?.loadingStateSolution) {
        dispatch(
          pushToLoadingStack({
            action: LoadingActionEnum.DECREASE,
            functionName: "home useEffect",
            date: new Date(),
          })
        );
        dispatch(decreasePendingOperations());
      } else {
        dispatch(setLoading(false));
      }
    }
  }, [franchiseId]);

  useEffect(() => {
    const prevId = prevSelectedFranchiseIdRef.current;
    prevSelectedFranchiseIdRef.current = selectedFranchise?.id;

    if (prevId !== undefined && !selectedFranchise) {
      // Same reasoning as the branch effect above: deselecting a franchise only
      // changes which price overrides apply, and props.allCategories carries
      // them all. No request needed to go back to base pricing.
      showMenuForFranchise(franchiseId > 0 ? franchiseId : 0);
    }
  }, [selectedFranchise?.id]);

  return (
    allCategories && (
      <>
        {!isMobile && <CartDrawer />}
        {globalSnackBar.isOpen && <SnackBar />}
        <PHead
          restaurantInfo={props.restaurantInfo}
          title={`${props.restaurantInfo && props.restaurantInfo.name}`}
        />
        <TrackingLogo />

        <CategoryScreen
          allCategories={allCategories}
          rerenderFlag={0}
          areaAboveNavBar={areaAboveNavBar}
        />
      </>
    )
  );
}

export const getStaticProps = async (context: any) => {
  const { restaurantInfo, socialMediaInfo } = await getRestaurantStaticProps();
  if (restaurantInfo === "N/A") {
    throw Error(
      `WRONG BUILD, /restaurant/${env.restaurantId}, ${restaurantInfo}}`
    );
  }
  const allCategories = await apiCache.fetchCall(
    `/category/details/${env.restaurantId}`,
    env.apiV2
  );
  if (!allCategories) {
    throw Error(
      `WRONG BUILD, /restaurant/${env.restaurantId}, ${restaurantInfo}, ${allCategories}}`
    );
  }

  return {
    props: {
      restaurantInfo,
      socialMediaInfo,
      allCategories,
      ...(await serverSideTranslations(context.locale, [
        ...globalTranslationFiles,
        "cart",
        "addons",
        "offers",
        "feedback",
        "pay",
        "delivery",
        "pointing-system",
        "wallet",
        "partner-account",
        "table-page",
        "referral",
        "common",
      ])),
    },
  };
};

Home.displayName = "Home";

export default Home;
